import {
  createApiRef,
  useApi,
  type ApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import type {
  FilterOptionsResponse,
  ListReviewsParams,
  PageResponse,
  ReviewFilters,
  ReviewIterationDetails,
  ReviewStatistics,
  ReviewStatusMessage,
  ReviewSummary,
} from '../types';

/** Defines the Code Reviewer data available to the dashboard. */
export interface PrReviewDashboardApi {
  /** Lists the reviews visible to the current user. */
  listReviews(params: ListReviewsParams): Promise<PageResponse<ReviewSummary>>;

  /** Gets the filter values available within the current selection. */
  getFilterOptions(filters: ReviewFilters): Promise<FilterOptionsResponse>;

  /** Gets finding totals by severity for the latest matching iterations. */
  getReviewStatistics(filters: ReviewFilters): Promise<ReviewStatistics>;

  /** Gets an exact review iteration, or the latest when no iteration is given. */
  getReviewIterationDetails(
    reviewId: string,
    iterationId?: number,
  ): Promise<ReviewIterationDetails>;

  /** Gets status changes when the user has permission to view them. */
  getReviewChangelog(reviewId: string): Promise<ReviewStatusMessage[]>;
}

/** Backstage API reference for the Code Reviewer frontend contract. */
export const prReviewDashboardApiRef: ApiRef<PrReviewDashboardApi> =
  createApiRef<PrReviewDashboardApi>({
    id: 'plugin.pr-review-dashboard.api',
  });

/**
 * Reads Code Reviewer data through the Backstage auth gateway.
 */
export class PrReviewDashboardApiClient implements PrReviewDashboardApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  async listReviews(
    params: ListReviewsParams,
  ): Promise<PageResponse<ReviewSummary>> {
    const search = this.buildFilterParams(params);
    search.set('page', String(params.page));
    search.set('size', String(params.size));
    search.set('sort', params.sort);
    search.set('sortDirection', params.direction);

    return this.request<PageResponse<ReviewSummary>>(
      `/reviews?${search.toString()}`,
    );
  }

  async getFilterOptions(
    filters: ReviewFilters,
  ): Promise<FilterOptionsResponse> {
    const search = this.buildFilterParams(filters);
    const query = search.toString();

    return this.request<FilterOptionsResponse>(
      query ? `/reviews/filters?${query}` : '/reviews/filters',
    );
  }

  async getReviewStatistics(filters: ReviewFilters): Promise<ReviewStatistics> {
    const search = this.buildFilterParams(filters);
    const query = search.toString();

    return this.request<ReviewStatistics>(
      query ? `/reviews/statistics?${query}` : '/reviews/statistics',
    );
  }

  async getReviewIterationDetails(
    reviewId: string,
    iterationId?: number,
  ): Promise<ReviewIterationDetails> {
    const search = new URLSearchParams();
    if (iterationId !== undefined) {
      search.set('iterationId', String(iterationId));
    }
    const query = search.toString();

    return this.request<ReviewIterationDetails>(
      `/reviews/${encodeURIComponent(reviewId)}/iterations${
        query ? `?${query}` : ''
      }`,
    );
  }

  async getReviewChangelog(reviewId: string): Promise<ReviewStatusMessage[]> {
    return this.request<ReviewStatusMessage[]>(
      `/reviews/${encodeURIComponent(reviewId)}/changelog`,
    );
  }

  private buildFilterParams(filters: ReviewFilters): URLSearchParams {
    const params = new URLSearchParams();
    filters.statuses.forEach(status => params.append('status', status));
    filters.organizations.forEach(owner =>
      params.append('repositoryOwners', owner),
    );
    filters.repositories.forEach(repository =>
      params.append('repositoryNames', repository),
    );
    filters.severities.forEach(severity =>
      params.append('containsSeverity', severity),
    );
    if (filters.branch) params.set('branch', filters.branch);
    if (filters.createdFrom) params.set('createdAfter', filters.createdFrom);
    if (filters.createdTo) params.set('createdBefore', filters.createdTo);
    return params;
  }

  private async request<T>(path: string): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('auth-gateway');

    const response = await this.fetchApi.fetch(
      `${baseUrl}/code-reviewer${path}`,
      {
        headers: {
          Accept: 'application/json',
        },
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }
}

/** Returns the PR Review Dashboard API implementation registered in Backstage. */
export const usePrReviewDashboardApi = (): PrReviewDashboardApi =>
  useApi(prReviewDashboardApiRef);
